import { baseUrl } from "../const/base-url";

export type RawProduct = {
  _id: string;
  name?: string;
  cost?: number | string;
  imageUrl?: string;
  composition?: string;
  taste?: string;
  size?: Record<
    string,
    { price: number | string; weight?: number; _id?: string }[]
  >;
  extra?: string[] | unknown[];

  uploadedFile?: { path?: string };
  image?: { path?: string };
};

export type Product = {
  id: string;
  title: string;
  price: number;
  imageUrl?: string;
  composition?: string;
  taste?: string;
  size?: Record<string, { price: number; weight?: number }[]>;
  extra?: string[];
};

const toNum = (v: unknown): number => {
  if (typeof v === "number") return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v.replace(",", "."));
    return Number.isFinite(n) ? n : NaN;
  }
  return NaN;
};

const buildImageUrl = (p: RawProduct): string | undefined => {
  if (p.imageUrl) return p.imageUrl;
  const path = p.uploadedFile?.path || p.image?.path;

  return undefined;
};

function normalizeProduct(p: RawProduct): Product | null {
  const price = toNum(p.cost);
  if (!Number.isFinite(price)) {
    return null;
  }

  let normSize: Product["size"] | undefined;
  if (p.size && typeof p.size === "object") {
    normSize = Object.fromEntries(
      Object.entries(p.size).map(([k, arr]) => [
        k,
        Array.isArray(arr)
          ? arr.map((it) => ({
              price: toNum(it?.price),
              weight: typeof it?.weight === "number" ? it.weight : undefined,
            }))
          : [],
      ])
    );
  }

  const normExtra =
    Array.isArray(p.extra) && p.extra.every((x) => typeof x === "string")
      ? (p.extra as string[])
      : undefined;

  return {
    id: p._id,
    title: p.name ?? "Без названия",
    price,
    imageUrl: buildImageUrl(p),
    composition: p.composition,
    size: normSize,
    taste: p.taste,
    extra: normExtra,
  };
}

export async function fetchProducts(): Promise<Product[]> {
  const res = await fetch(`${baseUrl}/api/products`, { method: "GET" });
  if (!res.ok) throw new Error("Ошибка загрузки продуктов");
  const data: RawProduct[] = await res.json();
  return data.map(normalizeProduct).filter((x): x is Product => x !== null);
}

export async function fetchProductsByCategory(
  categoryId: string
): Promise<Product[]> {
  const res = await fetch(`${baseUrl}/api/products/category/${categoryId}`, {
    method: "GET",
  });
  if (!res.ok) throw new Error("Не удалось загрузить товары категории");
  const data: RawProduct[] = await res.json();
  return data.map(normalizeProduct).filter((x): x is Product => x !== null);
}

export async function fetchProductsByIds(ids: string[]): Promise<Product[]> {
  const clean = (ids || []).map(String).filter(Boolean);
  if (clean.length === 0) return [];

  try {
    const qs = encodeURIComponent(clean.join(","));
    const res = await fetch(`${baseUrl}/api/products/by-ids?ids=${qs}`);
    if (res.ok) {
      const raw: RawProduct[] = await res.json();
      return raw.map(normalizeProduct).filter((x): x is Product => x !== null);
    }
  } catch {
    // игнор, пойдём фолбэком
  }

  const all = await fetchProducts();
  const set = new Set(clean);
  return all.filter((p) => set.has(p.id));
}
